import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import FeatherIcon from "feather-icons-react";
import { useSelector } from "react-redux";

const OrgUsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Add User Modal State
  const [addModal, setAddModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newRole, setNewRole] = useState("analyst");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);

  // Reset Password Modal State
  const [resetModal, setResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [resetPasswordVal, setResetPasswordVal] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(null);

  const token = localStorage.getItem("token");
  const currentRole = (localStorage.getItem("role") || "").toLowerCase();
  const currentEmail = localStorage.getItem("userEmail") || "";
  const currentDomain = currentEmail.includes("@") ? currentEmail.split("@")[1] : "";

  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "light" });
  const isDark = layoutMode === "dark";

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}org/users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await resp.json();
      if (resp.ok && data.status) {
        setUsers(data.users || []);
      } else {
        setError(data.error || "Failed to load team users");
      }
    } catch (err) {
      setError(err.message || "Network error loading users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleRoleChange = async (userId, newRoleVal) => {
    setError(null);
    setSuccessMsg(null);
    try {
      const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}org/users/${userId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRoleVal }),
      });
      const data = await resp.json();
      if (resp.ok && data.status) {
        setSuccessMsg(`Role updated to ${newRoleVal.replace("_", " ")}`);
        fetchUsers();
      } else {
        setError(data.error || "Failed to update role");
      }
    } catch (err) {
      setError(err.message || "Network error updating role");
    }
  };

  const handleToggleStatus = async (user) => {
    setError(null);
    setSuccessMsg(null);
    const currentStatus = String(user.Status || user.status || "active").toLowerCase().trim();
    const targetStatus = currentStatus === "active" ? "disabled" : "active";
    if (currentStatus === "active" && !window.confirm(`Are you sure you want to disable ${user.Email}?`)) {
      return;
    }
    try {
      const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}org/users/${user._id || user.UserID}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: targetStatus }),
      });
      const data = await resp.json();
      if (resp.ok && data.status) {
        setSuccessMsg(`User status updated to ${targetStatus}`);
        fetchUsers();
      } else {
        setError(data.error || "Failed to update user status");
      }
    } catch (err) {
      setError(err.message || "Network error toggling status");
    }
  };

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    setAddError(null);
    setAddLoading(true);

    try {
      const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}org/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: newEmail.trim().toLowerCase(),
          firstName: newFirstName.trim(),
          lastName: newLastName.trim(),
          role: newRole,
          password: newPassword,
        }),
      });

      const data = await resp.json();
      if (resp.ok && data.status) {
        setAddModal(false);
        setNewEmail("");
        setNewFirstName("");
        setNewLastName("");
        setNewPassword("");
        setSuccessMsg(`User ${data.user?.Email || newEmail} invited successfully with a temporary password.`);
        fetchUsers();
      } else {
        setAddError(data.error || "Failed to create user");
      }
    } catch (err) {
      setAddError(err.message || "Network error creating user");
    } finally {
      setAddLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setResetError(null);
    setResetLoading(true);

    try {
      const resp = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}org/users/${selectedUser._id || selectedUser.UserID}/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ newPassword: resetPasswordVal }),
        }
      );
      const data = await resp.json();
      if (resp.ok && data.status) {
        setResetModal(false);
        setResetPasswordVal("");
        setSelectedUser(null);
        setSuccessMsg("Temporary password set. The user will be required to change it upon login.");
        fetchUsers();
      } else {
        setResetError(data.error || "Failed to reset password");
      }
    } catch (err) {
      setResetError(err.message || "Network error resetting password");
    } finally {
      setResetLoading(false);
    }
  };

  const generateRandomPassword = () => {
    const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*";
    let pass = "";
    for (let i = 0; i < 12; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pass + "1!";
  };

  // Filtered users calculation
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      const name = `${u.FirstName || ""} ${u.LastName || ""}`.toLowerCase();
      const email = String(u.Email || "").toLowerCase();
      const query = searchQuery.trim().toLowerCase();
      const matchesSearch = !query || name.includes(query) || email.includes(query);

      const userRole = String(u.Role || "read_only").toLowerCase();
      const matchesRole = roleFilter === "all" || userRole === roleFilter;

      const userStatus = String(u.Status || u.status || "active").toLowerCase();
      const matchesStatus = statusFilter === "all" || userStatus === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, roleFilter, statusFilter]);

  // Metric stats
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => String(u.Status || "active").toLowerCase() === "active").length;
    const admins = users.filter((u) => String(u.Role || "").toLowerCase() === "tenant_admin").length;
    const analysts = users.filter((u) => String(u.Role || "").toLowerCase() === "analyst").length;
    const mustReset = users.filter((u) => !!u.MustChangePassword).length;
    return { total, active, admins, analysts, mustReset };
  }, [users]);

  // Pill badge renderers
  const renderStatusBadge = (status) => {
    const s = String(status || "active").toLowerCase().trim();
    if (s === "active") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 12px",
            borderRadius: "999px",
            fontSize: "12px",
            fontWeight: 600,
            background: isDark ? "rgba(16, 185, 129, 0.16)" : "#ecfdf5",
            color: isDark ? "#34d399" : "#059669",
            border: `1px solid ${isDark ? "rgba(16, 185, 129, 0.3)" : "#a7f3d0"}`,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#10b981",
              boxShadow: "0 0 6px #10b981",
            }}
          />
          Active
        </span>
      );
    }
    if (s === "disabled" || s === "inactive") {
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "4px 12px",
            borderRadius: "999px",
            fontSize: "12px",
            fontWeight: 600,
            background: isDark ? "rgba(239, 68, 68, 0.16)" : "#fef2f2",
            color: isDark ? "#f87171" : "#dc2626",
            border: `1px solid ${isDark ? "rgba(239, 68, 68, 0.3)" : "#fecaca"}`,
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#ef4444" }} />
          Disabled
        </span>
      );
    }
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 12px",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 600,
          background: isDark ? "rgba(245, 158, 11, 0.16)" : "#fffbeb",
          color: isDark ? "#fbbf24" : "#d97706",
          border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.3)" : "#fde68a"}`,
        }}
      >
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#f59e0b" }} />
        Pending
      </span>
    );
  };

  const renderRoleBadge = (role) => {
    switch (role) {
      case "tenant_admin":
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "3px 9px",
              borderRadius: "6px",
              fontSize: "11.5px",
              fontWeight: 700,
              background: isDark ? "rgba(225, 29, 72, 0.15)" : "#ffe4e6",
              color: isDark ? "#fb7185" : "#e11d48",
              border: `1px solid ${isDark ? "rgba(225, 29, 72, 0.3)" : "#fecdd3"}`,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            <FeatherIcon icon="shield" size={11} />
            Admin
          </span>
        );
      case "analyst":
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "3px 9px",
              borderRadius: "6px",
              fontSize: "11.5px",
              fontWeight: 700,
              background: isDark ? "rgba(59, 130, 246, 0.15)" : "#dbeafe",
              color: isDark ? "#60a5fa" : "#2563eb",
              border: `1px solid ${isDark ? "rgba(59, 130, 246, 0.3)" : "#bfdbfe"}`,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            <FeatherIcon icon="activity" size={11} />
            Analyst
          </span>
        );
      case "auditor":
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "3px 9px",
              borderRadius: "6px",
              fontSize: "11.5px",
              fontWeight: 700,
              background: isDark ? "rgba(20, 184, 166, 0.15)" : "#ccfbf1",
              color: isDark ? "#2dd4bf" : "#0d9488",
              border: `1px solid ${isDark ? "rgba(20, 184, 166, 0.3)" : "#99f6e4"}`,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            <FeatherIcon icon="eye" size={11} />
            Auditor
          </span>
        );
      case "read_only":
      default:
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              padding: "3px 9px",
              borderRadius: "6px",
              fontSize: "11.5px",
              fontWeight: 700,
              background: isDark ? "rgba(148, 163, 184, 0.12)" : "#f1f5f9",
              color: isDark ? "#94a3b8" : "#475569",
              border: `1px solid ${isDark ? "rgba(148, 163, 184, 0.25)" : "#cbd5e1"}`,
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            <FeatherIcon icon="lock" size={11} />
            Read Only
          </span>
        );
    }
  };

  if (currentRole !== "tenant_admin" && currentRole !== "super_admin") {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="warning" className="mt-4 shadow-sm" style={{ borderRadius: "10px" }}>
            <div className="d-flex align-items-center gap-3">
              <FeatherIcon icon="shield-off" size={24} className="text-warning flex-shrink-0" />
              <div>
                <h5 className="alert-heading mb-1" style={{ fontWeight: 600 }}>Access Restricted</h5>
                <p className="mb-0" style={{ fontSize: "14px" }}>
                  Team & User Governance is restricted to Organization Administrators (<code>tenant_admin</code>).
                  Please contact your security administrator if you require elevated privileges.
                </p>
              </div>
            </div>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ background: isDark ? "#0b1220" : "#f8fafc", minHeight: "100vh" }}>
      <MetaTags>
        <title>Team & User Management | GuardLogix ASM</title>
      </MetaTags>

      <Container fluid className="px-lg-4 py-3">
        {/* Top Header Banner */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 18px -4px rgba(37, 99, 235, 0.45)",
                color: "#ffffff",
                flexShrink: 0,
              }}
            >
              <FeatherIcon icon="users" size={24} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h3
                  className="mb-0"
                  style={{
                    fontWeight: 700,
                    fontSize: "22px",
                    letterSpacing: "-0.4px",
                    color: isDark ? "#f8fafc" : "#0f172a",
                  }}
                >
                  Organization Users
                </h3>
                {currentDomain && (
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: "6px",
                      background: isDark ? "rgba(37, 99, 235, 0.18)" : "#eff6ff",
                      color: isDark ? "#93c5fd" : "#1d4ed8",
                      fontSize: "12px",
                      fontWeight: 600,
                      border: `1px solid ${isDark ? "rgba(37, 99, 235, 0.3)" : "#bfdbfe"}`,
                    }}
                  >
                    @{currentDomain}
                  </span>
                )}
              </div>
              <p className="mb-0 text-muted" style={{ fontSize: "13.5px", marginTop: "3px" }}>
                Manage team access, assign role-based privileges, and control tenant security.
              </p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button
              color="light"
              onClick={fetchUsers}
              disabled={loading}
              className="d-flex align-items-center gap-2 shadow-sm"
              style={{
                height: "38px",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "13px",
                border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                background: isDark ? "#1e293b" : "#ffffff",
                color: isDark ? "#cbd5e1" : "#475569",
              }}
            >
              <FeatherIcon icon="refresh-cw" size={14} className={loading ? "spin" : ""} />
              <span>Refresh</span>
            </Button>
            <Button
              color="primary"
              onClick={() => {
                setAddError(null);
                setAddModal(true);
              }}
              className="d-flex align-items-center gap-2 shadow-sm"
              style={{
                height: "38px",
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "13.5px",
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                border: "none",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.35)",
              }}
            >
              <FeatherIcon icon="user-plus" size={15} />
              <span>Add Team Member</span>
            </Button>
          </div>
        </div>

        {/* Global Feedback Alerts */}
        {error && (
          <Alert color="danger" className="d-flex align-items-center py-2 mb-3 shadow-sm" style={{ borderRadius: "8px" }}>
            <FeatherIcon icon="alert-circle" size={16} className="me-2 flex-shrink-0" />
            <span style={{ fontSize: "13.5px" }}>{error}</span>
          </Alert>
        )}
        {successMsg && (
          <Alert color="success" className="d-flex align-items-center py-2 mb-3 shadow-sm" style={{ borderRadius: "8px" }}>
            <FeatherIcon icon="check-circle" size={16} className="me-2 flex-shrink-0" />
            <span style={{ fontSize: "13.5px" }}>{successMsg}</span>
          </Alert>
        )}

        {/* Quick Executive Metric Cards */}
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <div
              style={{
                padding: "16px 20px",
                borderRadius: "12px",
                background: isDark ? "#111a2e" : "#ffffff",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  background: isDark ? "rgba(59, 130, 246, 0.15)" : "#eff6ff",
                  color: isDark ? "#60a5fa" : "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FeatherIcon icon="users" size={20} />
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                  Total Users
                </div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                  {stats.total}
                </div>
              </div>
            </div>
          </Col>

          <Col xs={6} md={3}>
            <div
              style={{
                padding: "16px 20px",
                borderRadius: "12px",
                background: isDark ? "#111a2e" : "#ffffff",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  background: isDark ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5",
                  color: isDark ? "#34d399" : "#059669",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FeatherIcon icon="check-circle" size={20} />
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                  Active Accounts
                </div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                  {stats.active}
                </div>
              </div>
            </div>
          </Col>

          <Col xs={6} md={3}>
            <div
              style={{
                padding: "16px 20px",
                borderRadius: "12px",
                background: isDark ? "#111a2e" : "#ffffff",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  background: isDark ? "rgba(99, 102, 241, 0.15)" : "#eef2ff",
                  color: isDark ? "#818cf8" : "#4f46e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FeatherIcon icon="shield" size={20} />
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                  Privileged Roles
                </div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                  {stats.admins} <span style={{ fontSize: "14px", fontWeight: 500, color: "#94a3b8" }}>Admins / {stats.analysts} Analysts</span>
                </div>
              </div>
            </div>
          </Col>

          <Col xs={6} md={3}>
            <div
              style={{
                padding: "16px 20px",
                borderRadius: "12px",
                background: isDark ? "#111a2e" : "#ffffff",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  background: isDark ? "rgba(245, 158, 11, 0.15)" : "#fffbeb",
                  color: isDark ? "#fbbf24" : "#d97706",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FeatherIcon icon="key" size={20} />
              </div>
              <div>
                <div style={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase" }}>
                  Pending Reset
                </div>
                <div style={{ fontSize: "22px", fontWeight: 700, color: isDark ? "#f8fafc" : "#0f172a", lineHeight: 1.2 }}>
                  {stats.mustReset}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Users Table Card */}
        <Card
          className="border-0 shadow-sm"
          style={{
            borderRadius: "14px",
            background: isDark ? "#111a2e" : "#ffffff",
            border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
            overflow: "hidden",
          }}
        >
          {/* Card Filter Toolbar */}
          <div
            style={{
              padding: "16px 20px",
              borderBottom: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
              background: isDark ? "rgba(15, 23, 42, 0.4)" : "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "14px",
            }}
          >
            {/* Search Input */}
            <div style={{ position: "relative", minWidth: "260px", flex: "1 1 280px", maxWidth: "420px" }}>
              <FeatherIcon
                icon="search"
                size={15}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: isDark ? "#64748b" : "#94a3b8",
                }}
              />
              <Input
                type="text"
                placeholder="Search team members by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  paddingLeft: "36px",
                  height: "38px",
                  borderRadius: "8px",
                  fontSize: "13.5px",
                  background: isDark ? "#0f172a" : "#f8fafc",
                  borderColor: isDark ? "#334155" : "#cbd5e1",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }}
              />
            </div>

            {/* Dropdown Filters */}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "12.5px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b" }}>
                  Role:
                </span>
                <select
                  className="form-select form-select-sm"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={{
                    height: "36px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    background: isDark ? "#0f172a" : "#f8fafc",
                    borderColor: isDark ? "#334155" : "#cbd5e1",
                    color: isDark ? "#f8fafc" : "#0f172a",
                    minWidth: "130px",
                  }}
                >
                  <option value="all">All Roles</option>
                  <option value="tenant_admin">Tenant Admin</option>
                  <option value="analyst">Analyst</option>
                  <option value="auditor">Auditor</option>
                  <option value="read_only">Read Only</option>
                </select>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ fontSize: "12.5px", fontWeight: 600, color: isDark ? "#94a3b8" : "#64748b" }}>
                  Status:
                </span>
                <select
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    height: "36px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    background: isDark ? "#0f172a" : "#f8fafc",
                    borderColor: isDark ? "#334155" : "#cbd5e1",
                    color: isDark ? "#f8fafc" : "#0f172a",
                    minWidth: "120px",
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div style={{ fontSize: "12px", color: isDark ? "#64748b" : "#94a3b8", marginLeft: "6px" }}>
                Showing <strong>{filteredUsers.length}</strong> of {users.length}
              </div>
            </div>
          </div>

          <CardBody className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted" style={{ fontSize: "13.5px" }}>Loading team directory...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0" style={{ borderColor: isDark ? "#1e293b" : "#f1f5f9" }}>
                  <thead
                    style={{
                      background: isDark ? "#0c1322" : "#f8fafc",
                      borderBottom: `2px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
                    }}
                  >
                    <tr>
                      <th
                        className="ps-4 py-3"
                        style={{
                          fontSize: "11.5px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          color: isDark ? "#94a3b8" : "#64748b",
                          textTransform: "uppercase",
                        }}
                      >
                        Team Member
                      </th>
                      <th
                        className="py-3"
                        style={{
                          fontSize: "11.5px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          color: isDark ? "#94a3b8" : "#64748b",
                          textTransform: "uppercase",
                        }}
                      >
                        Access Role
                      </th>
                      <th
                        className="py-3"
                        style={{
                          fontSize: "11.5px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          color: isDark ? "#94a3b8" : "#64748b",
                          textTransform: "uppercase",
                        }}
                      >
                        Account Status
                      </th>
                      <th
                        className="py-3"
                        style={{
                          fontSize: "11.5px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          color: isDark ? "#94a3b8" : "#64748b",
                          textTransform: "uppercase",
                        }}
                      >
                        Password Reset
                      </th>
                      <th
                        className="text-end pe-4 py-3"
                        style={{
                          fontSize: "11.5px",
                          fontWeight: 700,
                          letterSpacing: "0.5px",
                          color: isDark ? "#94a3b8" : "#64748b",
                          textTransform: "uppercase",
                        }}
                      >
                        Security Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5 text-muted">
                          <FeatherIcon icon="user-x" size={28} className="mb-2 text-muted" />
                          <div style={{ fontSize: "14px", fontWeight: 500 }}>No users match the selected filters</div>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((u) => {
                        const isSelf = u.Email === currentEmail;
                        const userStatus = String(u.Status || u.status || "active").toLowerCase().trim();
                        const isActive = userStatus === "active";
                        const initials = (u.FirstName ? u.FirstName[0] : u.Email ? u.Email[0] : "U").toUpperCase();

                        return (
                          <tr
                            key={u._id || u.UserID}
                            style={{
                              transition: "background 0.15s ease",
                              background: isDark ? "transparent" : "#ffffff",
                            }}
                          >
                            {/* User Column */}
                            <td className="ps-4 py-3">
                              <div className="d-flex align-items-center">
                                <div
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "10px",
                                    background: isDark
                                      ? "linear-gradient(135deg, #1e293b, #0f172a)"
                                      : "linear-gradient(135deg, #eff6ff, #dbeafe)",
                                    color: isDark ? "#60a5fa" : "#2563eb",
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: `1px solid ${isDark ? "#334155" : "#bfdbfe"}`,
                                    marginRight: "14px",
                                    flexShrink: 0,
                                  }}
                                >
                                  {initials}
                                </div>
                                <div>
                                  <div className="d-flex align-items-center gap-2">
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "14px",
                                        color: isDark ? "#f8fafc" : "#0f172a",
                                      }}
                                    >
                                      {u.FirstName || u.LastName
                                        ? `${u.FirstName || ""} ${u.LastName || ""}`.trim()
                                        : u.Email}
                                    </span>
                                    {isSelf && (
                                      <span
                                        style={{
                                          padding: "1px 7px",
                                          borderRadius: "4px",
                                          fontSize: "11px",
                                          fontWeight: 700,
                                          background: isDark ? "rgba(37, 99, 235, 0.2)" : "#dbeafe",
                                          color: isDark ? "#93c5fd" : "#1d4ed8",
                                          letterSpacing: "0.3px",
                                        }}
                                      >
                                        YOU
                                      </span>
                                    )}
                                  </div>
                                  <small
                                    style={{
                                      fontSize: "12.5px",
                                      color: isDark ? "#94a3b8" : "#64748b",
                                      fontFamily: "var(--font-mono, monospace)",
                                    }}
                                  >
                                    {u.Email}
                                  </small>
                                </div>
                              </div>
                            </td>

                            {/* Role Column */}
                            <td className="py-3">
                              <div className="d-flex align-items-center gap-2">
                                {renderRoleBadge(u.Role)}
                                <select
                                  className="form-select form-select-sm"
                                  style={{
                                    width: "135px",
                                    height: "32px",
                                    fontSize: "12.5px",
                                    borderRadius: "6px",
                                    background: isDark ? "#0f172a" : "#f8fafc",
                                    borderColor: isDark ? "#334155" : "#cbd5e1",
                                    color: isDark ? "#f8fafc" : "#0f172a",
                                    fontWeight: 500,
                                    cursor: isSelf ? "not-allowed" : "pointer",
                                  }}
                                  value={u.Role}
                                  onChange={(e) => handleRoleChange(u._id || u.UserID, e.target.value)}
                                  disabled={isSelf}
                                  title={isSelf ? "Cannot change your own role" : undefined}
                                >
                                  <option value="tenant_admin">Tenant Admin</option>
                                  <option value="analyst">Analyst</option>
                                  <option value="auditor">Auditor</option>
                                  <option value="read_only">Read Only</option>
                                </select>
                              </div>
                            </td>

                            {/* Status Column */}
                            <td className="py-3">
                              {renderStatusBadge(u.Status)}
                            </td>

                            {/* Must Reset Pass Column */}
                            <td className="py-3">
                              {u.MustChangePassword ? (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    padding: "3px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11.5px",
                                    fontWeight: 600,
                                    background: isDark ? "rgba(245, 158, 11, 0.15)" : "#fffbeb",
                                    color: isDark ? "#fbbf24" : "#d97706",
                                    border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.3)" : "#fde68a"}`,
                                  }}
                                >
                                  <FeatherIcon icon="key" size={11} />
                                  Required
                                </span>
                              ) : (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "3px 8px",
                                    borderRadius: "6px",
                                    fontSize: "11.5px",
                                    fontWeight: 500,
                                    color: isDark ? "#64748b" : "#94a3b8",
                                  }}
                                >
                                  <FeatherIcon icon="check" size={12} />
                                  No
                                </span>
                              )}
                            </td>

                            {/* Actions Column */}
                            <td className="text-end pe-4 py-3">
                              <div className="d-inline-flex align-items-center gap-2">
                                <button
                                  className="btn btn-sm shadow-none"
                                  onClick={() => {
                                    setSelectedUser(u);
                                    setResetModal(true);
                                    setResetPasswordVal("");
                                    setResetError(null);
                                  }}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    height: "32px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    padding: "0 11px",
                                    background: isDark ? "#1e293b" : "#ffffff",
                                    borderColor: isDark ? "#334155" : "#cbd5e1",
                                    color: isDark ? "#e2e8f0" : "#334155",
                                    border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                                  }}
                                >
                                  <FeatherIcon icon="key" size={13} style={{ color: "#f59e0b" }} />
                                  <span>Reset Pass</span>
                                </button>

                                <button
                                  className="btn btn-sm shadow-none"
                                  disabled={isSelf}
                                  onClick={() => handleToggleStatus(u)}
                                  title={isSelf ? "Cannot disable your own administrator account" : undefined}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    height: "32px",
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    padding: "0 11px",
                                    background: isActive
                                      ? isDark ? "rgba(239, 68, 68, 0.12)" : "#fef2f2"
                                      : isDark ? "rgba(16, 185, 129, 0.12)" : "#ecfdf5",
                                    border: `1px solid ${
                                      isActive
                                        ? isDark ? "rgba(239, 68, 68, 0.3)" : "#fecaca"
                                        : isDark ? "rgba(16, 185, 129, 0.3)" : "#a7f3d0"
                                    }`,
                                    color: isActive
                                      ? isDark ? "#f87171" : "#dc2626"
                                      : isDark ? "#34d399" : "#059669",
                                    opacity: isSelf ? 0.45 : 1,
                                    cursor: isSelf ? "not-allowed" : "pointer",
                                  }}
                                >
                                  <FeatherIcon icon={isActive ? "slash" : "check"} size={13} />
                                  <span>{isActive ? "Disable" : "Enable"}</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* Add User Modal */}
      <Modal isOpen={addModal} toggle={() => setAddModal(!addModal)} centered>
        <ModalHeader
          toggle={() => setAddModal(!addModal)}
          style={{
            borderBottom: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
            background: isDark ? "#0f172a" : "#f8fafc",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <FeatherIcon icon="user-plus" size={18} className="text-primary" />
            <span style={{ fontWeight: 700, fontSize: "16px" }}>Add New Team Member</span>
          </div>
        </ModalHeader>
        <Form onSubmit={handleAddUserSubmit}>
          <ModalBody style={{ background: isDark ? "#111a2e" : "#ffffff", padding: "24px" }}>
            {addError && (
              <Alert color="danger" className="d-flex align-items-center py-2 mb-3">
                <FeatherIcon icon="alert-circle" size={16} className="me-2 flex-shrink-0" />
                <span style={{ fontSize: "13.5px" }}>{addError}</span>
              </Alert>
            )}

            <Row className="g-3 mb-3">
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label style={{ fontSize: "13px", fontWeight: 600 }}>First Name</Label>
                  <Input
                    type="text"
                    required
                    placeholder="John"
                    value={newFirstName}
                    onChange={(e) => setNewFirstName(e.target.value)}
                    style={{ borderRadius: "8px", height: "38px" }}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup className="mb-0">
                  <Label style={{ fontSize: "13px", fontWeight: 600 }}>Last Name</Label>
                  <Input
                    type="text"
                    placeholder="Doe"
                    value={newLastName}
                    onChange={(e) => setNewLastName(e.target.value)}
                    style={{ borderRadius: "8px", height: "38px" }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-3">
              <Label style={{ fontSize: "13px", fontWeight: 600 }}>Work Email</Label>
              <Input
                type="email"
                required
                placeholder={currentDomain ? `username@${currentDomain}` : "user@company.com"}
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                style={{ borderRadius: "8px", height: "38px" }}
              />
              {currentDomain && (
                <small className="text-muted d-block mt-1" style={{ fontSize: "12px" }}>
                  Must use organization email domain: <strong>@{currentDomain}</strong>
                </small>
              )}
            </FormGroup>

            <FormGroup className="mb-3">
              <Label style={{ fontSize: "13px", fontWeight: 600 }}>Assigned Role</Label>
              <Input
                type="select"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                style={{ borderRadius: "8px", height: "38px" }}
              >
                <option value="analyst">Analyst (Runs scans, manages findings, retests)</option>
                <option value="tenant_admin">Tenant Admin (Full organization & user management)</option>
                <option value="auditor">Auditor (Read-only findings & reports export)</option>
                <option value="read_only">Read Only (Inspection only, no mutations)</option>
              </Input>
            </FormGroup>

            <FormGroup className="mb-1">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Label style={{ fontSize: "13px", fontWeight: 600, marginBottom: 0 }}>Temporary Password</Label>
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary"
                  style={{ fontSize: "12px", textDecoration: "none" }}
                  onClick={() => setNewPassword(generateRandomPassword())}
                >
                  Generate Strong Pass
                </button>
              </div>
              <div style={{ position: "relative" }}>
                <Input
                  type={showNewPassword ? "text" : "password"}
                  required
                  minLength="8"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ borderRadius: "8px", height: "38px", paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    color: "#94a3b8",
                  }}
                >
                  <FeatherIcon icon={showNewPassword ? "eye-off" : "eye"} size={16} />
                </button>
              </div>
              <small className="text-muted d-block mt-1" style={{ fontSize: "12px" }}>
                The user will be required to change their temporary password upon their first login.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter
            style={{
              borderTop: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              background: isDark ? "#0f172a" : "#f8fafc",
            }}
          >
            <Button
              color="light"
              onClick={() => setAddModal(false)}
              style={{ borderRadius: "8px", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={addLoading}
              style={{
                borderRadius: "8px",
                fontWeight: 600,
                background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                border: "none",
              }}
            >
              {addLoading ? <Spinner size="sm" /> : "Create User"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={resetModal} toggle={() => setResetModal(!resetModal)} centered>
        <ModalHeader
          toggle={() => setResetModal(!resetModal)}
          style={{
            borderBottom: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
            background: isDark ? "#0f172a" : "#f8fafc",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <FeatherIcon icon="key" size={18} className="text-warning" />
            <span style={{ fontWeight: 700, fontSize: "16px" }}>Reset User Password</span>
          </div>
        </ModalHeader>
        <Form onSubmit={handleResetPasswordSubmit}>
          <ModalBody style={{ background: isDark ? "#111a2e" : "#ffffff", padding: "24px" }}>
            {resetError && (
              <Alert color="danger" className="d-flex align-items-center py-2 mb-3">
                <FeatherIcon icon="alert-circle" size={16} className="me-2 flex-shrink-0" />
                <span style={{ fontSize: "13.5px" }}>{resetError}</span>
              </Alert>
            )}

            <div
              style={{
                padding: "10px 14px",
                borderRadius: "8px",
                background: isDark ? "rgba(30, 41, 59, 0.6)" : "#f1f5f9",
                marginBottom: "16px",
                fontSize: "13px",
              }}
            >
              Resetting password for: <strong>{selectedUser?.Email}</strong>
            </div>

            <FormGroup className="mb-2">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Label style={{ fontSize: "13px", fontWeight: 600, marginBottom: 0 }}>New Temporary Password</Label>
                <button
                  type="button"
                  className="btn btn-link p-0 text-primary"
                  style={{ fontSize: "12px", textDecoration: "none" }}
                  onClick={() => setResetPasswordVal(generateRandomPassword())}
                >
                  Generate Strong Pass
                </button>
              </div>
              <div style={{ position: "relative" }}>
                <Input
                  type={showResetPassword ? "text" : "password"}
                  required
                  minLength="8"
                  placeholder="Min 8 characters"
                  value={resetPasswordVal}
                  onChange={(e) => setResetPasswordVal(e.target.value)}
                  style={{ borderRadius: "8px", height: "38px", paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    color: "#94a3b8",
                  }}
                >
                  <FeatherIcon icon={showResetPassword ? "eye-off" : "eye"} size={16} />
                </button>
              </div>
              <small className="text-muted d-block mt-1" style={{ fontSize: "12px" }}>
                The user will be required to set a new password on their next login.
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter
            style={{
              borderTop: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              background: isDark ? "#0f172a" : "#f8fafc",
            }}
          >
            <Button
              color="light"
              onClick={() => setResetModal(false)}
              style={{ borderRadius: "8px", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={resetLoading}
              style={{
                borderRadius: "8px",
                fontWeight: 600,
                background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                border: "none",
              }}
            >
              {resetLoading ? <Spinner size="sm" /> : "Update Password"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default OrgUsersManagement;
